import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { useModal } from '../context/ModalContext';
import '../styles/Logs.css';
import CustomSelect from './CustomSelect';
import { APP_VERSION } from '../config/version';

const Logs = ({ user }) => {
  const modal = useModal();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [level, setLevel] = useState('all');
  const [limit, setLimit] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'security', label: 'Security' },
    { value: 'auth', label: 'Authentication' },
    { value: 'squadcast', label: 'SquadCast' },
    { value: 'transcription', label: 'Transcription' },
    { value: 'ideas', label: 'Ideas' },
    { value: 'scheduler', label: 'Scheduler' },
    { value: 'general', label: 'General' }
  ];

  const levels = [
    { value: 'all', label: 'All Levels' },
    { value: 'INFO', label: 'Info' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'ERROR', label: 'Error' }
  ];

  const fetchLogs = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params = {
        category: category !== 'all' ? category : undefined,
        level: level !== 'all' ? level : undefined,
        limit
      };

      const response = await axios.get('/api/super-admin/logs', {
        params,
        withCredentials: true
      });

      setLogs(response.data.logs || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching logs:', err);
      if (!silent) {
        if (err.response?.status === 403) {
          modal.error('Access denied. Super admin privileges required.');
        } else {
          modal.error('Failed to load logs. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [category, level, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh every 10 seconds when enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLogs(true);
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  const handleClearLogs = async () => {
    const result = await modal.confirm('Are you sure you want to clear all logs?', 'Clear All Logs');
    if (!result) {
      return;
    }

    try {
      await axios.post('/api/super-admin/logs/clear', {}, { withCredentials: true });
      setLogs([]);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error clearing logs:', err);
      modal.error('Failed to clear logs');
    }
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }).replace(',', ' at');
  };

  const getLevelClass = (level) => {
    switch (level) {
      case 'ERROR':
        return 'log-level-error';
      case 'WARNING':
        return 'log-level-warning';
      case 'INFO':
        return 'log-level-info';
      default:
        return 'log-level-debug';
    }
  };

  const getCategoryClass = (category) => {
    switch (category) {
      case 'security':
        return 'log-category-security';
      case 'squadcast':
        return 'log-category-squadcast';
      case 'transcription':
        return 'log-category-transcription';
      case 'ideas':
        return 'log-category-ideas';
      case 'scheduler':
        return 'log-category-scheduler';
      case 'auth':
        return 'log-category-auth';
      default:
        return 'log-category-general';
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="logs-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="logs-container">
      <div className="logs-content-wrapper">
        <p className="logs-description">Monitor system events, errors, and security activities in real-time.</p>

        <div className="logs-filters">
          <div className="logs-filter-group">
            <span className="logs-filter-label">Category</span>
            <CustomSelect
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="logs-type-select"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </CustomSelect>
          </div>

          <div className="logs-filter-group">
            <span className="logs-filter-label">Level</span>
            <CustomSelect
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="logs-type-select"
              >
                {levels.map(lvl => (
                  <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                ))}
              </CustomSelect>
          </div>

          <div className="logs-filter-group">
            <span className="logs-filter-label">Limit</span>
            <CustomSelect
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="logs-type-select"
              >
                <option value={50}>Last 50</option>
                <option value={100}>Last 100</option>
                <option value={200}>Last 200</option>
                <option value={500}>Last 500</option>
              </CustomSelect>
          </div>

          <div className="logs-filter-group logs-actions">
            {autoRefresh && (
              <span className="auto-refresh-indicator">
                <span className="refresh-dot"></span>
                Auto-refreshing
              </span>
            )}
            <button
              className={`btn-auto-refresh ${autoRefresh ? 'active' : ''}`}
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
            >
              Auto
            </button>

            <button className="btn-refresh" onClick={() => fetchLogs()}>
              Refresh
            </button>

            <button className="btn-clear" onClick={handleClearLogs}>
              Clear
            </button>
          </div>
        </div>

        <div className="logs-status-row">
          <span className="last-updated">
            {lastUpdated && `Last updated: ${formatTimestamp(lastUpdated.toISOString())}`}
          </span>
          <span className="logs-version">Version {APP_VERSION}</span>
        </div>

        <div className="logs-table-card">
          {logs.length === 0 ? (
            <div className="empty-logs">
              <Icon name="feeds" size="3rem" />
              <h3>No Logs Found</h3>
              <p>No logs match the current filters, or the log buffer is empty.</p>
            </div>
          ) : (
            <div className="logs-list">
              {logs.map((log, index) => (
                <div key={index} className="log-entry">
                  <div className="log-meta">
                    <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
                    <span className={`log-level ${getLevelClass(log.level)}`}>{log.level}</span>
                    <span className={`log-category ${getCategoryClass(log.category)}`}>{log.category}</span>
                  </div>
                  <div className="log-message">{log.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Logs;
